import React from "react";
import { resolveMedia } from "@/lib/api";
import { Trophy, PencilSimple } from "@phosphor-icons/react";

const ROUND_CODE = { "Round of 16": "R16", "Quarterfinals": "QF", "Semifinals": "SF", "Championship": "F" };

const fmtDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
};
const fmtTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const TeamCell = ({ tid, teams, winner }) => {
  const t = tid ? teams[tid] : null;
  if (!t) return <span className="text-white/30 italic">TBD</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 ${winner ? "text-[#FF5722] font-bold" : ""}`}>
      {t.logo_url
        ? <img src={resolveMedia(t.logo_url)} alt="" className="w-5 h-5 object-contain shrink-0" />
        : <span className="w-5 h-5 flex items-center justify-center text-[10px] font-bold shrink-0" style={{ backgroundColor: t.primary_color || "#FF5722", color: "#000" }}>{(t.name || "?").charAt(0)}</span>}
      <span className="text-white/40 text-[11px]">#{t.seed}</span>
      <span className="uppercase font-semibold">{t.name}</span>
    </span>
  );
};

function StatusCell({ m }) {
  if (m.status === "completed") {
    return <span className="font-display text-xl tabular-nums text-[#FF5722]" data-testid={`playoff-sched-score-${m.id}`}>{m.a_score}–{m.b_score}</span>;
  }
  const bothSet = m.a_team_id && m.b_team_id;
  if (!bothSet) return <span className="text-[10px] uppercase tracking-wide font-bold text-white/30">Awaiting teams</span>;
  if (m.date) return <span className="text-[10px] uppercase tracking-wide font-bold text-[#FFEA00]">Scheduled</span>;
  return <span className="text-[10px] uppercase tracking-wide font-bold text-white/40">TBA</span>;
}

export function PlayoffScheduleTable({ bracket, onManage }) {
  if (!bracket || !bracket.rounds) return null;
  const teams = bracket.teams || {};
  return (
    <div className="space-y-6" data-testid="playoff-schedule">
      {bracket.rounds.map((rd) => {
        const code = ROUND_CODE[rd.name] || rd.name.slice(0, 2).toUpperCase();
        const matches = rd.matches.filter((m) => m.status !== "bye");
        if (matches.length === 0) return null;
        return (
          <div key={rd.round} data-testid={`playoff-schedule-round-${rd.round}`}>
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={15} weight="fill" className="text-[#FF5722]" />
              <h4 className="font-display text-xl uppercase tracking-wide">Playoffs — {rd.name}</h4>
            </div>
            <div className="border border-white/10 bg-[#121212] overflow-x-auto">
              <table className="w-full text-sm min-w-[680px]">
                <thead>
                  <tr className="border-b border-white/10 text-white/50 uppercase text-[11px] tracking-wider">
                    <th className="text-left py-3 px-4 w-16">Game</th>
                    <th className="text-left py-3 px-3">Date</th>
                    <th className="text-left py-3 px-3">Time</th>
                    <th className="text-left py-3 px-3">Court</th>
                    <th className="text-left py-3 px-3">Matchup</th>
                    <th className="text-left py-3 px-3 w-32">Score / Status</th>
                    {onManage && <th className="py-3 px-3 w-12" />}
                  </tr>
                </thead>
                <tbody>
                  {matches.map((m) => (
                    <tr key={m.id} data-testid={`playoff-sched-row-${m.id}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors">
                      <td className="py-3 px-4 font-display text-lg text-[#FF5722]">{code}{m.pos + 1}</td>
                      <td className="py-3 px-3 text-white/70 whitespace-nowrap">{m.date ? fmtDate(m.date) : "—"}</td>
                      <td className="py-3 px-3 text-white/70 whitespace-nowrap">{m.date ? fmtTime(m.date) : "—"}</td>
                      <td className="py-3 px-3 text-white/70 whitespace-nowrap">{m.court || "—"}</td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <TeamCell tid={m.a_team_id} teams={teams} winner={m.winner_team_id && m.winner_team_id === m.a_team_id} />
                          <span className="text-[#FF5722] text-[10px] font-bold uppercase">vs</span>
                          <TeamCell tid={m.b_team_id} teams={teams} winner={m.winner_team_id && m.winner_team_id === m.b_team_id} />
                        </div>
                      </td>
                      <td className="py-3 px-3"><StatusCell m={m} /></td>
                      {onManage && (
                        <td className="py-3 px-3 text-right">
                          <button onClick={() => onManage({ ...m, round: rd.round })} data-testid={`playoff-sched-manage-${m.id}`}
                            className="text-white/40 hover:text-[#FF5722] p-1 transition-colors" title="Schedule / edit this playoff game">
                            <PencilSimple size={17} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
